import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import './TabsLayout.scss';
import { Link } from 'react-router';
import { tabsArrowHandler, tabsTabKeyHandler } from './accessibility';
import A11y from 'chameleon/ui-stack/utilities/a11y';
import { keyNames } from 'chameleon/legacy/src/utility/accessibility';

/**
 * Draws tabs layout
 * */
const TabsLayout = ({ children, className }) => (
  <div className={cn('TabsLayout', className)}>{children}</div>
);

export const Tabs = ({ children, className, classNameChildren }) => (
  <div
    className={cn('TabsLayout__tabs', className)}
    role="tablist"
    aria-orientation="horizontal"
  >
    <div className={classNameChildren}>{children}</div>
  </div>
);

export const handleKeyDown = (event) => {
  if (
    [
      keyNames.arrowLeft,
      keyNames.arrowRight,
      keyNames.home,
      keyNames.end,
    ].includes(event.key)
  ) {
    tabsArrowHandler(event);
    return;
  }

  if (event.key === keyNames.tab) {
    tabsTabKeyHandler(event);
  }
};

export const Tab = ({
  path,
  isActive,
  text,
  onClick,
  onlyActiveOnIndex,
  children,
  className,
  disabled,
  id = null,
}) => {
  const cssClasses = cn({ active: isActive }, className || 'Tabs__link', 'tab');

  const handleFocus = (event) => {
    if (!isActive) {
      onClick(event);
    }
  };

  const handleKeyDownWrapper = (event) => {
    const container = event.currentTarget.closest('.TabsLayout__tabs');
    handleKeyDown(event, container);

    // Call childOnKeyDown if it exists
    const { onKeyDown: childOnKeyDown } = children.props || {};
    if (childOnKeyDown) {
      childOnKeyDown(event);
    }
  };

  const tabIndexValue = isActive ? '0' : '-1';

  if (path) {
    return (
      <A11y onKeyDown={handleKeyDownWrapper}>
        <Link
          onlyActiveOnIndex={onlyActiveOnIndex}
          to={path}
          className={cssClasses}
          onFocus={handleFocus}
        >
          {children || text}
        </Link>
      </A11y>
    );
  }

  return (
    <button
      aria-controls={`tabpanel-${id}`}
      id={`tab-${id}`}
      disabled={disabled}
      onClick={onClick}
      onFocus={handleFocus}
      onKeyDown={handleKeyDownWrapper}
      className={cssClasses}
      role="tab"
      aria-selected={isActive}
      tabIndex={tabIndexValue}
    >
      {children || text}
    </button>
  );
};

Tab.defaultProps = {
  onlyActiveOnIndex: true,
  onClick: () => {},
};

export const TabsContent = ({ children, className }) => (
  <div className={cn(className)}>{children}</div>
);

TabsLayout.displayName = 'TabsLayout';

TabsLayout.propTypes = {
  /**
   * Any child - dom element, react element, string, null
   * */
  children: PropTypes.any.isRequired,
  /**
   * Container class name
   * */
  className: PropTypes.string,
};

export default TabsLayout;
